import {v4 as uuidv4} from 'uuid';
import moment from 'moment';
import {AxiosResponse, AxiosStatic} from 'axios';
import {showGlobalMessage} from '../app/modules/_common/components/GlobalMessage/actions/showGlobalMessage';
import {messages as M} from '../_metronic/i18n/messages';
import {refreshToken} from '../app/modules/Auth/_redux/authActions';
import {AppStore} from './store';
import appConfig from '../config/common.json';

type CommonResponse = {
  errorCode: number,
  body: {errorMessage: string}
}

export default function setupAxios(axios: AxiosStatic, store: AppStore) {
  axios.interceptors.request.use(
    async config => {
      config.baseURL =
        (process.env.NODE_ENV === 'development')
          ? appConfig.dev_base_URL
          : appConfig.base_URL;
      config.headers['Content-Type'] = 'application/json';
      config.headers['requestid'] = `${uuidv4()}`;

      if (!config.url) {
        return config;
      }

      if (isWhitelisted(config.url, freeAccessURLs)) {
        return config;
      }

      if (isWhitelisted(config.url, onlyCurrentTokenURLs)) {
        config.headers.accessToken = getAccessTokenFromStore(store);
        return config;
      }

      const accessToken = await getAccessToken(store);
      if (accessToken) {
        config.headers.accessToken = `${accessToken}`;
      }
      return config;
    },
    error => {
      Promise.reject(error);
    }
  );
  axios.interceptors.response.use(
    (response: AxiosResponse<CommonResponse>) => {
      const {errorCode, body} = response.data;
      if (!(response.data instanceof Blob)) {

        if (!response.config.url) {
          return response;
        }

        const {errorMessage} = body;
        const isGeneralError = errorCode !== 0;
        const isInWhitelist = isWhitelisted(response.config.url, urlWhiteList);
        if (isGeneralError && !isInWhitelist) {
          const customMessage = M[errorCode.toString()];
          console.error({response});
          console.error('Ooops!', `error code ${errorCode}`, customMessage);
          Boolean(errorMessage)
            ? showGlobalMessage('error', errorMessage)(store.dispatch)
            : showGlobalMessage('error', customMessage)(store.dispatch);
        }
      }
      return response;
    },
    error => {
      const isAuthError = error.response.status >= 400;
      const isNotLogout = error.request.responseURL !== `${appConfig.base_URL}/auth/logout`;
      if (process.env.NODE_ENV !== 'development') {
        if (isAuthError && isNotLogout) window.location.href = '/logout';
      }

      return Promise.reject(error);
    });

}

const isWhitelisted = (url: string, whitelist: string[]) => {
  return whitelist.some(item => url.endsWith(item));
};

const urlWhiteList = [
  '/files/generate6101Close',
];

const onlyCurrentTokenURLs = [
  'logout'
];

const freeAccessURLs = [
  'auth/login',
  'auth/refreshToken', // refresh token call configured manually
];

const getAccessToken = async (store: AppStore) => {
  const expire = getExpireTimeFromStore(store);
  if (!expire || checkTokenExpire(expire)) {
    return await refreshToken(getAccessTokenFromStore(store))(store.dispatch);
  } else {
    return getAccessTokenFromStore(store);
  }
};

const getAccessTokenFromStore = (store: AppStore) => store.getState().auth.accessToken;
const getExpireTimeFromStore = (store: AppStore) => store.getState().auth.expire;

const checkTokenExpire = (expire: number) => {
  const expireMoment = moment.unix(expire).subtract(appConfig.refreshGap);
  const currentMoment = moment();

  return currentMoment.isSameOrAfter(expireMoment);
};